import { useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { usePageMutations, useProjectPages } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { generateRankBetween } from '@/lib/utils/lexorank'

interface UseCreatePageOptions {
  projectId: string
  projectKey: string
  workspaceSlug: string
  navigateToPage?: (slug: string) => void
}

export function useCreatePage({
  projectId,
  projectKey,
  workspaceSlug,
  navigateToPage,
}: UseCreatePageOptions) {
  const navigate = useNavigate()
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const userId = useLoginStore((state) => state.userId)
  const { pages } = useProjectPages(projectId)
  const { createPage } = usePageMutations()

  return useCallback(
    async (parentId: string | null = null) => {
      if (!workspaceId || !userId) return

      const newSlug = crypto.randomUUID().slice(0, 8)

      try {
        const siblings = (pages ?? [])
          .filter((p) => !p.isDeleted && (p.parentPageId || null) === parentId)
          .sort((a, b) => {
            if (a.sortOrder && b.sortOrder) return a.sortOrder.localeCompare(b.sortOrder)
            return a.createdOn - b.createdOn
          })
        const lastSortOrder = siblings.at(-1)?.sortOrder ?? null
        const nextSortOrder = generateRankBetween(lastSortOrder, null)

        await createPage({
          projectId,
          parentPageId: parentId,
          title: '',
          slug: newSlug,
          icon: null,
          iconType: null,
          sortOrder: nextSortOrder,
        })

        if (navigateToPage) {
          navigateToPage(newSlug)
        } else {
          navigate({
            to: '/$workspaceSlug/projects/$projectKey/pages/$pageSlug',
            params: { workspaceSlug, projectKey, pageSlug: newSlug },
          })
        }
      } catch {
        toast.error('Unable to create page')
      }
    },
    [workspaceId, userId, projectId, pages, createPage, navigate, navigateToPage, workspaceSlug, projectKey],
  )
}
